import { Checkbox, FormInstance } from 'antd';
import { useEffect, useState, useRef } from 'react';
import { PlusOutlined } from '@ant-design/icons';
import { InputCommon } from 'components/common/Input/InputCommon';
import SelectCommon from 'components/common/Select/SelectCommon';
import { ButtonCommon } from 'components/common/Button/ButtonCommon';
import { TrashSimple } from '@phosphor-icons/react';
import { Unit, UnitItem } from 'types';
import { Unit as UnitModel } from 'models';
import { useWatch } from 'antd/es/form/Form';

export interface UnitConversionListProps {
  form?: FormInstance;
  isEdit?: boolean;
  isView?: boolean;
  isInactive?: boolean;
  isEdits?: boolean;
  buttonName?: string;
  productUnits?: Unit[];
  setProductUnits?: (units: Unit[]) => void;
  units?: UnitModel[];
  isDetail?: boolean;
}

const UnitConversionList: React.FC<UnitConversionListProps> = ({
  form,
  isEdit = true,
  isView = false,
  isInactive = false,
  buttonName = 'Thêm',
  setProductUnits,
  productUnits = [],
  units = [],
  isDetail = false,
}) => {
  const [items, setItems] = useState<UnitItem[]>([]);
  const [saleUnit, setSaleUnit] = useState<UnitModel | undefined>();
  const saleUnitId = useWatch('baseUnitId', form);
  const salePrice = useWatch('salePrice', form) || 0;
  const costPrice = useWatch('costPrice', form) || 0;
  const isExternalUpdate = useRef(false);
  const isInitialized = useRef(false);
  const prevProductUnits = useRef<Unit[]>([]);
  const hasInitializedItems = useRef(false);

  // This effect runs only once for initialization of default units on create
  useEffect(() => {
    // Skip if already initialized
    if (hasInitializedItems.current) return;

    // For create mode: initialize with all units
    if (!isDetail && units.length > 0) {
      const defaultItems = units.map((unit) => ({
        id: unit.id,
        checked: true,
        quantity: 1,
        label: unit.name || '',
        isNew: false,
      }));

      setItems(defaultItems);
      hasInitializedItems.current = true;
      isInitialized.current = true;
    }

    // For detail mode: if no initial productUnits, initialize with all units unchecked
    else if (isDetail && units.length > 0 && productUnits.length === 0) {
      setItems([]);
      hasInitializedItems.current = true;
      isInitialized.current = true;
    }
  }, [isDetail, units, productUnits.length]);
  // This effect handles updates to productUnits (from external sources)
  useEffect(() => {
    const hasChanged = JSON.stringify(productUnits) !== JSON.stringify(prevProductUnits.current);

    if (productUnits.length > 0 && hasChanged) {
      isExternalUpdate.current = true;

      const unitItems = productUnits.map((unit) => ({
        id: unit.unitId as string,
        checked: true,
        quantity: unit.convertValue || 0,
        label: unit.label || '',
        isNew: false,
      }));

      setItems(unitItems);
      prevProductUnits.current = [...productUnits];
      isInitialized.current = true;
      hasInitializedItems.current = true;

      setTimeout(() => {
        isExternalUpdate.current = false;
      }, 100);
    }
  }, [productUnits]);

  // Remaining effects and handlers
  useEffect(() => {
    if (saleUnitId) {
      setSaleUnit(units?.find((unit) => unit.id === saleUnitId));
    } else {
      setSaleUnit(undefined);
    }
  }, [saleUnitId, units]);

  useEffect(() => {
    if (setProductUnits && !isExternalUpdate.current && isInitialized.current) {
      const updatedUnits = items.map(
        (item, order) =>
          ({
            unitId: item.id,
            checked: item.checked,
            convertValue: item.quantity ? Number(item.quantity) : 0,
            costPrice: costPrice * (item.quantity ? Number(item.quantity) : 0),
            salePrice: salePrice * (item.quantity ? Number(item.quantity) : 0),
            label: item.label || '',
            order: order + 1,
            isNew: item.isNew || false,
          }) as Unit,
      );

      const hasLengthChanged = updatedUnits.length !== prevProductUnits.current.length;
      const hasContentChanged = JSON.stringify(updatedUnits) !== JSON.stringify(prevProductUnits.current);

      if (hasLengthChanged || hasContentChanged) {
        setProductUnits(updatedUnits);
        prevProductUnits.current = [...updatedUnits];
      }
    }
  }, [items, salePrice, costPrice, setProductUnits]);

  const handleAdd = () => {
    const newItem = {
      id: Date.now().toString(),
      checked: true,
      quantity: 0,
      label: '',
      isNew: true,
    };
    setItems((prev) => [...prev, newItem]);
  };

  const handleChange = ({ id, key, value }: { id: string; key: string; value: string | boolean }) => {
    setItems((prev) => prev.map((item) => (item.id === id ? { ...item, [key]: value } : item)));
  };

  const handleDelete = (id: string) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  };

  return (
    <div>
      {!isView && <p className='text-[var(--textBlack56)] leading-5'>Đơn vị đặc thù khác</p>}
      <div className='mt-3'>
        <div className='flex flex-col gap-3'>
          {items.map((item) => (
            <div key={item.id} className='flex flex-wrap gap-4 items-center'>
              <div className='flex items-center gap-2 w-[160px]'>
                <Checkbox
                  checked={item.checked}
                  onChange={(e) => handleChange({ id: item.id, key: 'checked', value: e.target.checked })}
                  disabled={!isEdit || isInactive}
                />
                {item.checked ? (
                  <InputCommon
                    disabled={!isEdit}
                    type='text'
                    className='min-w-[130px] !text-[var(--textBlack100)]'
                    placeholder='Tên đơn vị đặc thù'
                    // twSize='sm2'
                    value={!item.checked && item.label === '' ? '---' : item.label}
                    onChange={(e) => handleChange({ id: item.id, key: 'label', value: e.target.value })}
                  />
                ) : (
                  <span className='text-[13px] text-black font-medium'>{item.label || '---'}</span>
                )}
              </div>

              <span className='text-[var(--textBlack56)] leading-5'>Giá trị quy đổi:</span>
              <div className='flex gap-3 items-center'>
                <InputCommon
                  type='decimal'
                  disabled={!isEdit || isInactive}
                  className='min-w-[214px] !text-[var(--textBlack100)]'
                  placeholder='Nhập giá trị quy đổi...'
                  // twSize='sm2'
                  value={!item.checked && item.quantity === 0 ? '---' : item.quantity}
                  onChange={(e) => handleChange({ id: item.id, key: 'quantity', value: e.target.value })}
                />

                <SelectCommon
                  disabled={true}
                  className='min-w-[214px]'
                  statusSelect={'disabled'}
                  value={saleUnit?.id || ''}
                  options={[{ label: saleUnit?.name || '---', value: saleUnit?.id || '' }]}
                  selectType='select'
                  // sizeSelect='md2'
                />
                {isEdit && (
                  <div>
                    <TrashSimple size={24} onClick={() => handleDelete(item.id)} className='cursor-pointer' />
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {!isView && (
        <div className='mt-3 max-w-[208px]'>
          <ButtonCommon
            color='basic'
            fullWidth
            radius='rounded'
            size='md'
            className='bg-[var(--bgBlackOpacity)] px-3 text-black gap-2 '
            onClick={handleAdd}
          >
            <PlusOutlined style={{ fontSize: '16px' }} />
            <span className='whitespace-nowrap'>{buttonName}</span>
          </ButtonCommon>
        </div>
      )}
    </div>
  );
};

export default UnitConversionList;
